import { useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Shield, Users, DollarSign, Settings, Loader2, Save, Ban, CheckCircle2, Receipt } from "lucide-react";

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function Metrics() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)).catch(() => {}); }, []);
  if (!m) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  const cards = [
    { label: "Professores", value: m.total_teachers, color: "text-brand-blue bg-blue-50" },
    { label: "Assinantes ativos", value: m.active_subscribers, color: "text-brand-green bg-emerald-50" },
    { label: "Em teste grátis", value: m.trial_users, color: "text-brand-purple bg-purple-50" },
    { label: "MRR", value: fmt(m.mrr), color: "text-brand-yellow bg-amber-50" },
    { label: "Receita Pix", value: fmt(m.revenue_pix), color: "text-brand-green bg-emerald-50" },
    { label: "Receita Cartão", value: fmt(m.revenue_card), color: "text-brand-blue bg-blue-50" },
    { label: "Receita Assinaturas", value: fmt(m.revenue_subscription), color: "text-brand-purple bg-purple-50" },
    { label: "Receita Avulsos", value: fmt(m.revenue_single), color: "text-brand-yellow bg-amber-50" },
    { label: "Quizzes vendidos", value: m.quizzes_sold, color: "text-brand-blue bg-blue-50" },
    { label: "Assinaturas pendentes", value: m.pending_subs, color: "text-brand-yellow bg-amber-50" },
    { label: "Assinaturas canceladas", value: m.canceled_subs, color: "text-brand-muted bg-slate-100" },
    { label: "Pagamentos aprovados", value: m.payments_approved, color: "text-brand-green bg-emerald-50" },
    { label: "Recusados/Cancelados", value: m.payments_rejected, color: "text-red-500 bg-red-50" },
    { label: "Reembolsados", value: m.payments_refunded, color: "text-brand-muted bg-slate-100" },
    { label: "Quizzes criados", value: m.total_quizzes, color: "text-brand-blue bg-blue-50" },
    { label: "Questões IA (mês)", value: m.ai_generated_this_month, color: "text-brand-purple bg-purple-50" },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <p className="font-heading text-2xl font-black text-brand-ink">{c.value}</p>
          <p className="text-xs text-brand-muted mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

function Plans() {
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/admin/plans").then((r) => setPlans(r.data));
  useEffect(() => { load(); }, []);

  const update = (idx, field, val) => {
    const p = [...plans];
    p[idx] = { ...p[idx], [field]: val };
    setPlans(p);
  };

  const save = async (plan) => {
    try {
      await api.put(`/admin/plans/${plan.plan_id}`, {
        name: plan.name,
        price_monthly: Number(plan.price_monthly),
        price_yearly: Number(plan.price_yearly),
        max_players: Number(plan.max_players),
        ai_quota: Number(plan.ai_quota),
      });
      toast.success(`Plano ${plan.name} atualizado.`);
    } catch {
      toast.error("Erro ao salvar plano.");
    }
  };

  if (!plans) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  return (
    <div className="space-y-4">
      {plans.map((p, i) => (
        <div key={p.plan_id} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid={`admin-plan-${p.plan_id}`}>
          <div className="grid sm:grid-cols-5 gap-4 items-end">
            <div className="sm:col-span-1">
              <Label className="text-xs">Nome</Label>
              <Input value={p.name} onChange={(e) => update(i, "name", e.target.value)} className="mt-1 rounded-xl h-10" />
            </div>
            <div>
              <Label className="text-xs">Preço mensal</Label>
              <Input type="number" step="0.01" value={p.price_monthly} onChange={(e) => update(i, "price_monthly", e.target.value)} className="mt-1 rounded-xl h-10" data-testid={`price-${p.plan_id}`} />
            </div>
            <div>
              <Label className="text-xs">Preço anual</Label>
              <Input type="number" step="0.01" value={p.price_yearly} onChange={(e) => update(i, "price_yearly", e.target.value)} className="mt-1 rounded-xl h-10" />
            </div>
            <div>
              <Label className="text-xs">Máx. jogadores</Label>
              <Input type="number" value={p.max_players} onChange={(e) => update(i, "max_players", e.target.value)} className="mt-1 rounded-xl h-10" data-testid={`players-${p.plan_id}`} />
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Cota IA</Label>
                <Input type="number" value={p.ai_quota} onChange={(e) => update(i, "ai_quota", e.target.value)} className="mt-1 rounded-xl h-10" data-testid={`quota-${p.plan_id}`} />
              </div>
              <Button onClick={() => save(p)} size="icon" className="rounded-xl h-10 w-10 bg-brand-green hover:bg-emerald-600" data-testid={`save-plan-${p.plan_id}`}><Save className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const toggleBlock = async (u) => {
    await api.put(`/admin/users/${u.user_id}/block`);
    toast.success(u.blocked ? "Usuário desbloqueado." : "Usuário bloqueado.");
    load();
  };

  if (!users) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-brand-muted">
          <tr>
            <th className="text-left p-4 font-medium">Nome</th>
            <th className="text-left p-4 font-medium hidden sm:table-cell">E-mail</th>
            <th className="text-left p-4 font-medium">Papel</th>
            <th className="text-left p-4 font-medium hidden sm:table-cell">Plano</th>
            <th className="text-right p-4 font-medium">Ações</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-t border-slate-100" data-testid={`user-row-${u.user_id}`}>
              <td className="p-4 font-medium text-brand-ink">{u.name}</td>
              <td className="p-4 text-brand-muted hidden sm:table-cell">{u.email}</td>
              <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-slate-100">{u.role}</span></td>
              <td className="p-4 text-brand-muted hidden sm:table-cell">{u.subscription_status || "—"}</td>
              <td className="p-4 text-right">
                {u.role !== "admin" && (
                  <Button variant="outline" size="sm" onClick={() => toggleBlock(u)} className={`rounded-full gap-1 ${u.blocked ? "text-brand-green" : "text-red-500"}`} data-testid={`block-${u.user_id}`}>
                    {u.blocked ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                    {u.blocked ? "Desbloquear" : "Bloquear"}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => {
    try {
      await api.put("/admin/settings", {
        single_quiz_price: Number(s.single_quiz_price),
        openai_model: s.openai_model,
        trial_days: Number(s.trial_days),
      });
      toast.success("Configurações salvas.");
    } catch { toast.error("Erro ao salvar."); }
  };
  if (!s) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  return (
    <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm max-w-lg space-y-4">
      <div>
        <Label>Preço do quiz avulso (R$)</Label>
        <Input type="number" step="0.01" value={s.single_quiz_price} onChange={(e) => setS({ ...s, single_quiz_price: e.target.value })} className="mt-1.5 rounded-xl h-11" />
      </div>
      <div>
        <Label>Modelo OpenAI</Label>
        <Input value={s.openai_model} onChange={(e) => setS({ ...s, openai_model: e.target.value })} className="mt-1.5 rounded-xl h-11" />
      </div>
      <div>
        <Label>Dias de teste grátis</Label>
        <Input type="number" value={s.trial_days} onChange={(e) => setS({ ...s, trial_days: e.target.value })} className="mt-1.5 rounded-xl h-11" />
      </div>
      <Button onClick={save} className="rounded-full bg-brand-blue hover:bg-blue-700 gap-2"><Save className="w-4 h-4" /> Salvar</Button>
    </div>
  );
}

const methodLabel = { pix: "Pix", card: "Cartão" };
const kindLabel = { single_quiz: "Quiz avulso", subscription: "Assinatura" };
const payStatusColor = {
  approved: "text-brand-green bg-emerald-50",
  pending: "text-brand-yellow bg-amber-50",
  rejected: "text-red-500 bg-red-50",
  cancelled: "text-red-500 bg-red-50",
  canceled: "text-red-500 bg-red-50",
  refunded: "text-brand-muted bg-slate-100",
};

function PaymentsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/payments").then((r) => setData(r.data)).catch(() => setData({ payments: [], pix_orders: [] })); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  const { payments = [], pix_orders = [] } = data;
  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-heading font-bold text-brand-ink mb-3">Pagamentos confirmados</h3>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-brand-muted">
              <tr>
                <th className="text-left p-4 font-medium">Data</th>
                <th className="text-left p-4 font-medium">Cliente</th>
                <th className="text-left p-4 font-medium">Tipo</th>
                <th className="text-left p-4 font-medium">Método</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-right p-4 font-medium">Valor</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-brand-muted">Nenhum pagamento registrado.</td></tr>
              )}
              {payments.map((p, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`admin-payment-${i}`}>
                  <td className="p-4 text-brand-muted">{p.created_at ? new Date(p.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="p-4 text-brand-ink">{p.user_email || p.user_id || "—"}</td>
                  <td className="p-4">{kindLabel[p.kind] || p.kind || "—"}</td>
                  <td className="p-4">{methodLabel[p.method] || p.method || "—"}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${payStatusColor[p.status] || "bg-slate-100"}`}>{p.status}</span></td>
                  <td className="p-4 text-right font-semibold text-brand-ink">{fmt(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-heading font-bold text-brand-ink mb-3">Cobranças Pix (todas)</h3>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-brand-muted">
              <tr>
                <th className="text-left p-4 font-medium">Data</th>
                <th className="text-left p-4 font-medium">Cliente</th>
                <th className="text-left p-4 font-medium">Tipo</th>
                <th className="text-left p-4 font-medium">Status</th>
                <th className="text-left p-4 font-medium">Liberado</th>
                <th className="text-right p-4 font-medium">Valor</th>
              </tr>
            </thead>
            <tbody>
              {pix_orders.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-brand-muted">Nenhuma cobrança Pix.</td></tr>
              )}
              {pix_orders.map((o, i) => (
                <tr key={i} className="border-t border-slate-100" data-testid={`admin-pix-${i}`}>
                  <td className="p-4 text-brand-muted">{o.created_at ? new Date(o.created_at).toLocaleDateString("pt-BR") : "—"}</td>
                  <td className="p-4 text-brand-ink">{o.user_email || o.user_id || "—"}</td>
                  <td className="p-4">{kindLabel[o.kind] || o.kind || "—"}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full ${payStatusColor[o.status] || "bg-slate-100"}`}>{o.status}</span></td>
                  <td className="p-4">{o.content_released ? <CheckCircle2 className="w-4 h-4 text-brand-green" /> : "—"}</td>
                  <td className="p-4 text-right font-semibold text-brand-ink">{fmt(o.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <DashboardLayout>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-ink to-slate-700 flex items-center justify-center text-white"><Shield className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-3xl font-black text-brand-ink">Administração</h1>
          <p className="text-brand-muted text-sm">Gestão da plataforma EDUCARTE</p>
        </div>
      </div>
      <Tabs defaultValue="metrics">
        <TabsList className="rounded-full bg-white border border-slate-100 p-1 h-auto flex-wrap">
          <TabsTrigger value="metrics" className="rounded-full gap-1.5" data-testid="tab-metrics"><DollarSign className="w-4 h-4" /> Indicadores</TabsTrigger>
          <TabsTrigger value="plans" className="rounded-full gap-1.5" data-testid="tab-plans"><Settings className="w-4 h-4" /> Planos</TabsTrigger>
          <TabsTrigger value="payments" className="rounded-full gap-1.5" data-testid="tab-payments"><Receipt className="w-4 h-4" /> Pagamentos</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full gap-1.5" data-testid="tab-users"><Users className="w-4 h-4" /> Usuários</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full gap-1.5" data-testid="tab-settings"><Settings className="w-4 h-4" /> Configurações</TabsTrigger>
        </TabsList>
        <TabsContent value="metrics" className="mt-6"><Metrics /></TabsContent>
        <TabsContent value="plans" className="mt-6"><Plans /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsTab /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
